import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTelemetry } from '../hooks/useTelemetry';

/**
 * NavigationTracker component automatically tracks route changes
 * Should be placed near the top of the component tree to capture all navigation
 */
const NavigationTracker: React.FC = () => {
  const location = useLocation();
  const { trackNavigation, trackEvent } = useTelemetry('NavigationTracker');
  const previousLocation = useRef<string>('');
  const sessionStartTime = useRef<number>(Date.now());

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    // Track page view
    trackEvent('page-view', {
      path: currentPath,
      timestamp: Date.now(),
      sessionDuration: Date.now() - sessionStartTime.current,
      referrer: document.referrer,
      userAgent: navigator.userAgent.substring(0, 100) // Truncate for privacy
    });

    // Track navigation if we have a previous location
    if (previousLocation.current && previousLocation.current !== currentPath) {
      trackNavigation(previousLocation.current, currentPath, {
        timestamp: Date.now(),
        sessionDuration: Date.now() - sessionStartTime.current,
        navigationType: getNavigationType()
      });
    }

    // Update previous location
    previousLocation.current = currentPath;
  }, [location, trackNavigation, trackEvent]);

  // Track initial page load
  useEffect(() => {
    trackEvent('session-start', {
      initialPath: location.pathname + location.search,
      timestamp: sessionStartTime.current,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform
    });

    // Track session end on page unload
    const handleBeforeUnload = () => {
      trackEvent('session-end', {
        finalPath: location.pathname + location.search,
        sessionDuration: Date.now() - sessionStartTime.current,
        timestamp: Date.now()
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [trackEvent, location.pathname, location.search]);

  return null; // This component doesn't render anything
};

/**
 * Determine the type of navigation based on browser navigation API
 */
function getNavigationType(): string {
  if ('navigation' in window && 'type' in (window as any).navigation) {
    return (window as any).navigation.type;
  }
  
  // Fallback for browsers without Navigation API
  if (performance.navigation) {
    switch (performance.navigation.type) {
      case performance.navigation.TYPE_NAVIGATE:
        return 'navigate';
      case performance.navigation.TYPE_RELOAD:
        return 'reload';
      case performance.navigation.TYPE_BACK_FORWARD:
        return 'back_forward';
      default:
        return 'unknown';
    }
  }
  
  return 'unknown';
}

export default NavigationTracker; 